import React, { useState, useEffect } from "react";
import { inject, observer } from "mobx-react";
import { Col, Typography, List, Skeleton, Spin, message as messageToast } from "antd";
import CardIcon from "./CardIcon";
import AlertModal from "components/Modals/AlertModal";
import VerificationModal from "components/Modals/Verification";

const CardSelect = props => {
  const { userStore, authStore, values, setFieldValue } = props;
  const { getCards, deleteCard, sendCardOtp } = userStore;

  const [initLoading, setInitLoading] = useState(false);
  const [cardsList, setCardsList] = useState();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const [total, setTotal] = useState(0);
  const [cards, setCards] = useState([]);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pendingCard, setPendingCard] = useState(null);
  const [verifyModal, setVerifyModal] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  useEffect(() => {
    setInitLoading(true);
    getCards()
      .then(res => {
        if (res.status && res.data.length > 0) {
          setTotal(res.data.length);
          setCards(res.data);
          setCardsList(res.data.slice(0, pageSize));
          setFieldValue("card", res.data[0].id);
          setFieldValue("savedCardCount", res.data.length);
        } else {
          setFieldValue("card", "NewCard");
          setFieldValue("savedCardCount", 0);
        }
      })
      .finally(() => {
        setInitLoading(false);
      });
  }, []);

  function checkExprieDate(month, year) {
    let today = new Date();
    let expDate = new Date(year, month);
    return today < expDate;
  }

  const clearOtpFlow = () => {
    setVerifyModal(false);
    setPendingCard(null);
    setOtpMessage("");
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    const card = deleteConfirm;
    setDeleteConfirm(null);
    setOtpSending(true);
    try {
      const res = await sendCardOtp({ action: "delete" });
      if (res?.status) {
        setPendingCard(card);
        setOtpMessage(res.message || "Enter the OTP we sent you");
        setVerifyModal(true);
      }
      // Failed responses: the global API interceptor shows the error
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpSubmit = async otpValues => {
    if (!pendingCard) return;
    const res = await deleteCard({ card: pendingCard.id, otp: otpValues.otp });
    if (res?.data?.otpInvalid) {
      throw new Error(res.message || "Invalid OTP");
    }
    if (!res?.status) {
      throw new Error(res?.message || "Failed to remove card");
    }

    if (res?.data?.autoCreditDisabled && authStore) {
      authStore.applyUserFields({ autoCredit: false });
    }
    messageToast.success(res.message || "Card removed");

    const newCards = cards.filter(card => card.id !== pendingCard.id);
    setCards(newCards);
    setTotal(newCards.length);
    setPage(1);
    setCardsList(newCards.slice(0, pageSize));
    setFieldValue("savedCardCount", newCards.length);
    if (values.card === pendingCard.id) {
      setFieldValue("card", newCards.length > 0 ? newCards[0].id : "NewCard");
    }
    clearOtpFlow();
  };

  if (initLoading) {
    return (
      <Col span={24}>
        <Skeleton active />
      </Col>
    );
  }

  if (total == 0) {
    return null;
  }

  return (
    <>
      <Col span={24}>
        <List
          style={{
            margin: "0 auto"
          }}
          pagination={{
            onChange: page => {
              setPage(page);
              setCardsList(cards.slice((page - 1) * pageSize, page * pageSize));
            },
            defaultCurrent: 1,
            current: page,
            total: total,
            pageSize: pageSize,
            showSizeChanger: false,
            hideOnSinglePage: true
          }}
          loading={initLoading}
          itemLayout="horizontal"
          dataSource={cardsList}
          renderItem={item => (
            <List.Item
              onClick={() => {
                if (checkExprieDate(item.exp_month, item.exp_year)) {
                  setFieldValue("card", item.id);
                  setFieldValue("token", undefined);
                  setFieldValue("source", undefined);
                  setFieldValue("isNewCard", false);
                }
              }}
              style={{
                cursor: checkExprieDate(item.exp_month, item.exp_year) ? "pointer" : "inherit"
              }}
              className={`rounded-3 mb-3 px-2 bg-white ${
                values.card === item.id && !values?.isNewCard ? "border-info-color" : !checkExprieDate(item.exp_month, item.exp_year) ? "border-danger-color" : "border-muted-color"
              }`}
              actions={[
                <>
                  {otpSending && pendingCard?.id === item.id ? (
                    <Spin size="small" />
                  ) : (
                    <i
                      className="fas fa-trash-alt fs-6 pe-auto text-danger-color"
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setDeleteConfirm(item);
                      }}
                    />
                  )}
                </>
              ]}
            >
              <List.Item.Meta
                className="align-items-center"
                avatar={<CardIcon brand={item.brand.toLowerCase()} />}
                title={
                  <Typography.Text strong className="fs-5 ms-2 ms-md-5">
                    **** {item.last4}
                  </Typography.Text>
                }
              />
            </List.Item>
          )}
        />
      </Col>
      {total < 10 && (
        <Col span={24}>
          <Typography.Title className="fs-5 text-center text-grey" level={4}>
            OR
          </Typography.Title>
        </Col>
      )}

      <AlertModal
        visible={!!deleteConfirm}
        title="Remove card?"
        description={
          deleteConfirm
            ? `Remove ${(deleteConfirm.brand || "card").toUpperCase()} ending in ${deleteConfirm.last4}? You will need to enter an OTP to confirm.${
                deleteConfirm.isDefault ? " This is your default card for purchases and auto-credit — another saved card will be used if available." : ""
              }`
            : ""
        }
        primaryBtnProps={{
          label: "Continue",
          loading: otpSending,
          onClick: handleDeleteConfirm
        }}
        secondaryBtnProps={{
          label: "Cancel",
          onClick: () => setDeleteConfirm(null)
        }}
        onCancel={() => setDeleteConfirm(null)}
      />

      {verifyModal && (
        <VerificationModal
          visible={verifyModal}
          onCancel={clearOtpFlow}
          title={
            <div className="text-center">
              <h4 className="pt-3 fw-bold">Enter OTP</h4>
              <p className="mb-0">{otpMessage}</p>
            </div>
          }
          resendOtp={({ setCount, setAlert }) => {
            sendCardOtp({ action: "delete" }).then(res => {
              if (res?.status) {
                setOtpMessage(res.message || otpMessage);
                setCount(110);
                setAlert(true);
              }
            });
          }}
          onSubmit={handleOtpSubmit}
        />
      )}
    </>
  );
};

export default inject(stores => ({
  userStore: stores.store.userStore,
  authStore: stores.store.authStore
}))(observer(CardSelect));
